import { Card, CardHeader, CardTitle, CardBody, Chip, Badge } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Mail } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import {
  useServiceConfigoptions,
  useServiceAddonConfigoptions,
  type ServiceConfigOption,
  type ServiceDetail,
} from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** Flattened row: a base or addon configoption. */
interface EmailRow extends ServiceConfigOption {
  _addonName?: string;
}

const muted = { color: 'var(--vn-fg-muted)' } as const;
const rightHeader = (label: string) => () => (
  <span style={{ display: 'block', textAlign: 'right' }}>{label}</span>
);
const num = (v?: number | null) => (
  <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
    {v != null ? v : <span style={muted}>—</span>}
  </span>
);

export interface EmailConfigTableProps {
  detail: ServiceDetail;
}

/**
 * Email service information config table — shared by Email.EGPlatform and Email.Server.
 *
 * Both legacy Information components (`EGPlatform/Information` and
 * `EmailServer/Information`) render an identical DataTableCustomized with the same
 * column set (item / type / unit / commitment / prices / billing). The only
 * difference in the legacy view is the `defaultTemplate` string passed to
 * `transformServiceDetailData` and the `ServiceTechnicalComponent` suffix label
 * ("Domain/ IP"). That label is out of scope here — the lead wires it separately.
 *
 * Columns: Item / Type / Unit / Commitment / Unit price / Total / Billing / Available.
 */
export function EmailConfigTable({ detail }: EmailConfigTableProps) {
  const id = detail.service_id;
  const currency = detail.service_currency || 'VND';
  const base = useServiceConfigoptions(id);
  const addons = useServiceAddonConfigoptions(id);

  if (base.isError) return <TabError title="Failed to load configuration" onRetry={() => base.refetch()} />;
  if (base.isLoading) return <TabLoading rows={4} />;

  const money = (v?: number) => (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {formatMoney(v, currency)}
    </span>
  );

  const rows: EmailRow[] = [
    ...(base.data?.results ?? []),
    ...(addons.data?.results ?? []).flatMap((a) =>
      (a.configoptions ?? []).map((c) => ({ ...c, _addonName: a.service_name })),
    ),
  ];

  const hasAvailable = rows.some((r) => r.commitment_available != null);

  const columns: ColumnDef<EmailRow, unknown>[] = [
    {
      id: 'name',
      header: 'Item',
      cell: ({ row }) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name}</span>
          {row.original._addonName ? (
            <Badge tone="info" appearance="subtle" size="sm">
              addon: {row.original._addonName}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { id: 'type', header: 'Type', cell: ({ row }) => row.original.type || <span style={muted}>—</span> },
    { id: 'unit', header: 'Unit', cell: ({ row }) => row.original.unit || <span style={muted}>—</span> },
    { id: 'commitment_value', header: rightHeader('Commitment'), cell: ({ row }) => num(row.original.commitment_value) },
    { id: 'unit_price', header: rightHeader('Unit price'), cell: ({ row }) => money(row.original.commitment_price_per_unit) },
    { id: 'total', header: rightHeader('Total'), cell: ({ row }) => money(row.original.commitment_price_total) },
    {
      id: 'billing',
      header: 'Billing',
      cell: ({ row }) => (
        <Chip
          appearance="subtle"
          tone={row.original.commitment_prepaid ? 'success' : 'neutral'}
          size="sm"
        >
          {row.original.commitment_prepaid ? 'Prepaid' : 'Postpaid'}
        </Chip>
      ),
    },
  ];

  if (hasAvailable) {
    columns.push({
      id: 'available',
      header: rightHeader('Available'),
      cell: ({ row }) => num(row.original.commitment_available),
    });
  }

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
      </CardHeader>
      <CardBody>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => `${r.service_id}-${r.service_configoption_id}`}
          emptyState={
            <EmptyState
              icon={<Mail size={24} />}
              title="No configuration"
              description="No email configoptions found for this service."
            />
          }
        />
      </CardBody>
    </Card>
  );
}
